"use client";

import { Minus, Plus } from "@/components/icons";
import { cn } from "@/lib/utils";
import { useQuantityStepper } from "./use-quantity-stepper";

interface QuantityStepperProps {
  quantity: number;
  minQuantity: number;
  onUpdateQuantity: (quantity: number) => void | Promise<void>;
  onPendingChange?: (pending: boolean) => void;
  className?: string;
  size?: "default" | "lg";
}

const STEPPER_SIZES = {
  default: {
    input: "h-7 w-16 border-x border-border text-sm",
    button: "h-7 w-7",
    icon: "size-3",
  },
  lg: {
    input: "h-11 w-16 rounded-sm bg-card text-base",
    button: "h-11 w-11",
    icon: "size-4",
  },
} as const;

export function QuantityStepper(props: QuantityStepperProps) {
  const { minQuantity, className, size = "default" } = props;
  const sizing = STEPPER_SIZES[size];
  const { localQty, base, commit, handleChange, handleBlur } =
    useQuantityStepper(props);

  return (
    <div
      className={cn(
        "flex items-center rounded-md",
        size === "lg" ? "bg-muted p-0.5" : "border border-border",
        className,
      )}
    >
      <StepButton
        label="Decrease quantity"
        className={sizing.button}
        disabled={base <= minQuantity}
        onClick={() => commit(Math.max(minQuantity, base - 1))}
      >
        <Minus className={sizing.icon} />
      </StepButton>
      <input
        type="text"
        inputMode="numeric"
        value={localQty}
        aria-label="Quantity"
        className={cn(
          "bg-transparent text-center font-medium tabular-nums focus:outline-none",
          sizing.input,
        )}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <StepButton
        label="Increase quantity"
        className={sizing.button}
        onClick={() => commit(base + 1)}
      >
        <Plus className={sizing.icon} />
      </StepButton>
    </div>
  );
}

function StepButton({
  label,
  className,
  disabled,
  onClick,
  children,
}: {
  label: string;
  className: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      className={cn(
        "flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40",
        className,
      )}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
